
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { Authenticated } from "./components/Auth/Authenticated";
import AuthFlow from "./components/Auth/AuthFlow";
import { PublicRoute } from "./components/Auth/PublicRoute";
import NavBar from "./components/Navbar/NavBar";
import { AuthConsumer, AuthProvider } from "./context/JWTAuthContext";
import Home from "./components/Home/Home";
import AddTrack from "./components/Music/AddTrack";
import Contact from "./components/Home/Contact";
import DashboardLayout from "./components/Dashboard/DashboardLayout";
import ArtistTracks from "./components/Dashboard/artist/ArtistTracks";
import ArtistProfileWrapper from "./components/Dashboard/artist/ArtistProfileWrapper";
import Cart from "./components/Home/Cart";
import { CartProvider } from "./context/CartContext";
import Track from "./components/Home/Track";
import { Flex, Spinner } from "@chakra-ui/react";
import Artist from "./components/Home/Artist";
import FanProfile from "./components/Dashboard/fan/FanProfile";
import CompanyInfoStep from "./components/Auth/Merchant/CompanyInfoStep";
import MerchantDashboard from "./components/Dashboard/Merchant/MerchantDashboard";
import MerchantProfile from "./components/Dashboard/Merchant/MerchantProfile";
import ArtistCreationForm from "./components/Dashboard/Merchant/ArtistCreationForm";
import ArtistList from "./components/Dashboard/Merchant/ArtistList";
import Checkout from "./components/Home/Checkout";
import WalletCheckout from "./components/Wallet/WalletCheckout";
import OrderConfirmation from "./components/Home/OrderConfirmation";
import Faq from "./components/Navbar/Faq";
import About from "./components/Home/About";
import Documents from "./components/Dashboard/Documents";
import Portfolio from "./components/Dashboard/Portfolio";
import Transaction from "./components/Dashboard/Transaction";
import Browse from "./components/Home/Browse";
import PasswordReset from "./components/Auth/PasswordReset";
import { MusicPlayerProvider } from "./context/MusicPlayerContext";
import SubscriptionPage from "./components/Subscription/Subscriptions";
import SubscriptionCheckout from "./components/Subscription/SubscriptionCheckout";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import SalesData from "./components/Dashboard/artist/Sales";
import BillingInfo from "./components/Dashboard/BillingInfo";
import BillingAndPayments from "./components/Dashboard/BillingAndPayments";
import { BannerProvider } from "./context/BannerContext";
import ErrorPage from "./utils/Error";
import ScrollToTop from "./utils/ScrollToTop";
import ReferralDashboard from "./components/Dashboard/ReferralDashboard";
import WalletDashboard from "./components/Wallet/WalletDashboard";
import MetaTags from "./MetaTags";
import client from "./services/apolloClient";
import { ApolloProvider } from "@apollo/client";
import { WalletProvider } from "./context/WalletContext";
import { CreditProvider } from "./context/CreditContext";
import { MyFeaturedTracks } from "./components/FeaturedTracks";



const stripePromise = loadStripe('pk_test_51PLQgX2KnKdf1YCEVojH8ER1TVelnSOzUmuNJIJaOadbq1EatVpO2RVndyWTAX0CUmp7fomagqJFkkCQpOJuJB9O00kq1zQolv');


function App() {

  return (
    <>
      <ApolloProvider client={client}>
    
  
        <BannerProvider>
    
              <AuthProvider>
            <CartProvider>
                <MusicPlayerProvider>
                  <SubscriptionProvider>
                    <WalletProvider>
                    <CreditProvider>
                    <Router>
                    <ScrollToTop/>
                      <AuthConsumer>
                        {(auth) =>
                          !auth.isInitialized ? (
                            <Flex height="100vh" alignItems="center" justifyContent="center">
                              <Spinner thickness="4px" speed="0.65s" emptyColor="primary.100" color="secondary.500" size="xl" />
                            </Flex>
                          ) : (
                            <>

<MetaTags/>
                         
                            <Routes>
                              {/* Main Application Routes */}
                         
                              <Route path="/" element={<NavBar />}>
                                {/* Public Routes */}
                                <Route index element={<Home />} />
                                <Route path="browse" element={<Browse />} />
                                <Route path="login" element={<PublicRoute><AuthFlow /></PublicRoute>} />
                                <Route path="register" element={<PublicRoute><AuthFlow /></PublicRoute>} />
                                <Route path="artist-details" element={<Authenticated><AuthFlow /></Authenticated>} />
                                <Route path="merchant/company-info" element={<Authenticated><CompanyInfoStep /></Authenticated>} />
                                <Route path="reset-password" element={<PasswordReset />} />
                                <Route path="faq" element={<Faq />} />
                                <Route path="about" element={<About />} />
                                <Route path="contact" element={<Contact />} />

                                {/* Authenticated Routes */}
                                <Route path="create-music" element={<Authenticated><AddTrack /></Authenticated>} />

                                {/* Merchant Standalone Routes */}
                                <Route path="merchant/dashboard" element={<Authenticated><MerchantDashboard /></Authenticated>} />
                                <Route path="merchant/artists" element={<Authenticated><ArtistList /></Authenticated>} />
                                <Route path="merchant/artists/create" element={<Authenticated><ArtistCreationForm /></Authenticated>} />

                                {/* Dashboard and Nested Routes */}
                                <Route path="dashboard" element={<Authenticated><DashboardLayout /></Authenticated>}>
                                  {/* Artist Routes */}
                                  <Route path="artist/profile" element={<ArtistProfileWrapper />} />
                                  <Route path="artist/tracks" element={<ArtistTracks />} />
                                  <Route path="artist/featured-tracks" element={<MyFeaturedTracks />} />
                                  <Route path="artist/documents" element={<Documents />} />
                                  <Route path="artist/sales" element={<SalesData />} />

                                  {/* Merchant Routes */}
                                  <Route path="merchant/profile" element={<MerchantProfile />} />
                                  <Route path="merchant/tracks" element={<ArtistTracks />} />
                                  <Route path="merchant/featured-tracks" element={<MyFeaturedTracks />} />
                                  <Route path="merchant/documents" element={<Documents />} />

                                  {/* Fan Routes */}
                                  <Route path="fan/profile" element={<Elements stripe={stripePromise}><FanProfile /></Elements>} />
                                  <Route path="collections" element={<Portfolio />} />
                                  <Route path="transactions" element={<Transaction />} />
                                  <Route path="billing" element={<BillingAndPayments />}>

                                 
                                    <Route path="info" element={<BillingInfo />} />
                   
                                    {auth.user && auth.user.account_type === "artist" && (
                                      <Route path="sales" element={<SalesData />} />
                                    )}
                                    <Route path="" element={<Transaction />} />
                                  </Route>
                                  <Route path="*" element={<ErrorPage statusCode={404} />} />
                                </Route>

                                {/* Cart and Checkout */}
                                <Route path="cart" element={<Cart />} />
                                <Route path="subscriptions" element={<SubscriptionPage />} />
                                <Route path="subscriptions/checkout" element={<Authenticated><Elements stripe={stripePromise}><SubscriptionCheckout /></Elements></Authenticated>} />
                                <Route path="checkout" element={<Authenticated><Elements stripe={stripePromise}><WalletCheckout /></Elements></Authenticated>} />
                                <Route path="order-confirmation" element={<Authenticated><OrderConfirmation /></Authenticated>} />
                                <Route path="referral" element={<ReferralDashboard/>} />
                                <Route path="wallet" element={<Authenticated><Elements stripe={stripePromise}><WalletDashboard /></Elements></Authenticated>} />

                                {/* Track and Artist Details */}
                                <Route path="track/:trackId" element={<Track />} />
                                <Route path="artist/:artistId" element={<Artist />} />
                              </Route>

                              {/* Redirect for Unknown Routes */}
                              <Route path="*" element={<ErrorPage statusCode={404} />} />
                             
                            </Routes>
                            </>

                          )
                        }
                      </AuthConsumer>
                    </Router>
                    </CreditProvider>
                    </WalletProvider>
                  </SubscriptionProvider>
                </MusicPlayerProvider>
            </CartProvider>
              </AuthProvider>


        </BannerProvider>
     </ApolloProvider>

    </>
  );
}

export default App;
